import type { FlowDocument, FlowNode, FlowNodeKind } from '@emailforge/schemas';
import { Button, Chip, Input, Select } from '@emailforge/ui';
import { type CSSProperties, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon, type IconName } from '@/components/ui/icons';
import {
  type AutomationRun,
  useGetAutomationFlowQuery,
  useListAutomationRunsQuery,
  useListAutomationsQuery,
  useSaveAutomationFlowMutation,
} from '@/features/automations/automationsApi';
import { t } from '@/lib/i18n';

const NODE_LIBRARY: Array<{ kind: FlowNodeKind; icon: IconName; label: string; description: string }> = [
  { kind: 'trigger', icon: 'flame', label: 'Trigger', description: 'Entry point: webhook / integration / schedule' },
  { kind: 'send', icon: 'send', label: 'Send', description: 'Send a template via SMTP' },
  { kind: 'delay', icon: 'beaker', label: 'Delay', description: 'Wait N minutes/hours/days' },
  { kind: 'split', icon: 'ab', label: 'A/B split', description: 'Randomly branch by weight' },
  { kind: 'condition', icon: 'variable', label: 'Condition', description: 'If/else on payload field' },
  { kind: 'end', icon: 'check', label: 'End', description: 'Terminal node' },
];

const STARTER_FLOW: FlowDocument = {
  version: 1,
  nodes: [
    {
      id: 'n1',
      kind: 'trigger',
      position: { x: 80, y: 120 },
      config: { source: 'shopify', event: 'order.paid' },
    },
    {
      id: 'n2',
      kind: 'send',
      position: { x: 360, y: 120 },
      config: { templateId: '00000000-0000-0000-0000-000000000000' },
    },
    {
      id: 'n3',
      kind: 'delay',
      position: { x: 640, y: 120 },
      config: { amount: 24, unit: 'hours' },
    },
  ],
  edges: [
    { id: 'e1', from: 'n1', to: 'n2' },
    { id: 'e2', from: 'n2', to: 'n3' },
  ],
};

export function AutomationAdvanced() {
  const { automationId = '' } = useParams();
  const navigate = useNavigate();
  const { data: automations } = useListAutomationsQuery();
  const automation = automations?.find((a) => a.id === automationId);

  const { data: serverFlow, isLoading: isLoadingFlow } = useGetAutomationFlowQuery(automationId, {
    skip: !automationId,
  });
  const { data: runs } = useListAutomationRunsQuery(automationId, {
    skip: !automationId,
    pollingInterval: 5000,
  });
  const [saveFlow, saveMut] = useSaveAutomationFlowMutation();

  const [doc, setDoc] = useState<FlowDocument>(STARTER_FLOW);
  const [selectedId, setSelectedId] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!serverFlow) return;
    const isEmpty = serverFlow.nodes.length === 0;
    setDoc(isEmpty ? STARTER_FLOW : serverFlow);
    setDirty(false);
  }, [serverFlow]);

  const selected = doc.nodes.find((n) => n.id === selectedId) ?? null;

  const updateNode = (id: string, mutator: (n: FlowNode) => FlowNode) => {
    setDoc((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (n.id === id ? mutator(n) : n)),
    }));
    setDirty(true);
  };

  const addNode = (kind: FlowNodeKind) => {
    const id = newId();
    const position = nextPosition(doc.nodes);
    const newNode = defaultNode(id, kind, position);
    setDoc((d) => ({ ...d, nodes: [...d.nodes, newNode] }));
    setSelectedId(id);
    setDirty(true);
  };

  const removeNode = (id: string) => {
    setDoc((d) => ({
      version: 1,
      nodes: d.nodes.filter((n) => n.id !== id),
      edges: d.edges.filter((e) => e.from !== id && e.to !== id),
    }));
    setSelectedId('');
    setDirty(true);
  };

  const onNodeDrag = (id: string, dx: number, dy: number) => {
    updateNode(id, (n) => ({
      ...n,
      position: { x: n.position.x + dx, y: n.position.y + dy },
    }));
  };

  const handlePublish = async () => {
    setError(null);
    try {
      const res = await saveFlow({ id: automationId, flow: doc }).unwrap();
      setDoc(res.flow);
      setDirty(false);
      setSavedAt(new Date());
    } catch (e: unknown) {
      setError(
        (e as { data?: { message?: string } })?.data?.message ??
          'Could not save flow. Check your nodes for required fields.',
      );
    }
  };

  const status = saveMut.isLoading
    ? 'Publishing…'
    : dirty
      ? 'Unsaved changes'
      : savedAt
        ? `Saved ${relativeTime(savedAt)}`
        : 'Up to date';
  const statusColor = saveMut.isLoading
    ? 'var(--ink-3)'
    : dirty
      ? 'var(--warn)'
      : 'var(--spark)';

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.automations'), automation?.name ?? t('crumbs.flow')]}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ fontSize: 11, color: statusColor }}>
              ● {status}
            </span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/automations')}>
              Back to list
            </Button>
            <Button variant="flame" size="sm" onClick={handlePublish}
              disabled={saveMut.isLoading || !dirty}>
              <Icon.check size={13} /> Publish flow
            </Button>
          </div>
        }
      />

      {error && (
        <div
          style={{
            padding: '10px 18px',
            background: 'var(--flame-soft)',
            borderBottom: '1px solid var(--flame-line)',
            color: 'var(--flame-2)',
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 1fr 320px',
          height: 'calc(100vh - 44px)',
          background: 'var(--paper-2)',
        }}
      >
        <aside style={{ background: 'var(--paper)', borderRight: '1px solid var(--line)', overflowY: 'auto' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-soft)' }}>
            <div
              className="mono"
              style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
            >
              Add node
            </div>
          </div>
          <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {NODE_LIBRARY.map((k) => {
              const IconCmp = Icon[k.icon];
              return (
                <button
                  key={k.kind}
                  className="mf-card"
                  onClick={() => addNode(k.kind)}
                  style={{
                    padding: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    border: '1px solid var(--line)',
                    background: 'var(--paper)',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ color: 'var(--flame)', paddingTop: 2 }}>
                    <IconCmp size={13} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 550 }}>{k.label}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>{k.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div
          style={{
            position: 'relative',
            overflow: 'auto',
            background: 'radial-gradient(circle at 20px 20px, var(--line-soft) 1.2px, transparent 1.3px) 0 0 / 20px 20px',
          }}
          onClick={() => setSelectedId('')}
        >
          {isLoadingFlow && (
            <div style={{ padding: 40, color: 'var(--ink-3)', fontSize: 12 }}>Loading flow…</div>
          )}

          <svg width={1600} height={600} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            <defs>
              <marker id="arrow" markerWidth={10} markerHeight={10} refX={8} refY={5} orient="auto">
                <path d="M0,0 L0,10 L10,5 z" fill="var(--ink-3)" />
              </marker>
            </defs>
            {doc.edges.map((e) => {
              const from = doc.nodes.find((n) => n.id === e.from);
              const to = doc.nodes.find((n) => n.id === e.to);
              if (!from || !to) return null;
              const x1 = from.position.x + 200;
              const y1 = from.position.y + 32;
              const x2 = to.position.x;
              const y2 = to.position.y + 32;
              const mx = (x1 + x2) / 2;
              return (
                <g key={e.id}>
                  <path
                    d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                    stroke="var(--ink-3)"
                    strokeWidth={1.3}
                    fill="none"
                    markerEnd="url(#arrow)"
                  />
                  {e.branch && (
                    <text
                      x={mx}
                      y={(y1 + y2) / 2 - 6}
                      textAnchor="middle"
                      fontSize={11}
                      fontFamily="var(--font-mono)"
                      fill="var(--flame-2)"
                    >
                      {e.branch}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {doc.nodes.map((n) => (
            <NodeCard
              key={n.id}
              node={n}
              selected={selectedId === n.id}
              onSelect={() => setSelectedId(n.id)}
              onDrag={(dx, dy) => onNodeDrag(n.id, dx, dy)}
            />
          ))}

          <RunsStrip runs={runs ?? []} />
        </div>

        <aside style={{ background: 'var(--paper)', borderLeft: '1px solid var(--line)', overflowY: 'auto' }}>
          <div style={{ padding: 16 }}>
            <div
              className="mono"
              style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
            >
              Inspector
            </div>
            {!selected && (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
                Select a node to configure it.
              </div>
            )}
            {selected && (
              <Inspector
                node={selected}
                onChange={(patch) => updateNode(selected.id, (n) => ({ ...n, ...patch } as FlowNode))}
                onConfigChange={(configPatch) =>
                  updateNode(selected.id, (n) => ({
                    ...n,
                    config: { ...(n.config as object), ...configPatch },
                  }) as FlowNode)
                }
                onDelete={() => removeNode(selected.id)}
              />
            )}
          </div>
        </aside>
      </div>
    </>
  );
}

function NodeCard({
  node,
  selected,
  onSelect,
  onDrag,
}: {
  node: FlowNode;
  selected: boolean;
  onSelect: () => void;
  onDrag: (dx: number, dy: number) => void;
}) {
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  const IconCmp =
    node.kind === 'trigger'
      ? Icon.flame
      : node.kind === 'send'
        ? Icon.send
        : node.kind === 'delay'
          ? Icon.beaker
          : node.kind === 'split'
            ? Icon.ab
            : node.kind === 'condition'
              ? Icon.variable
              : Icon.check;

  const { label, subtitle } = describeNode(node);

  return (
    <div
      onMouseDown={(e) => {
        e.stopPropagation();
        onSelect();
        setDragStart({ x: e.clientX, y: e.clientY });
      }}
      onMouseMove={(e) => {
        if (!dragStart) return;
        onDrag(e.clientX - dragStart.x, e.clientY - dragStart.y);
        setDragStart({ x: e.clientX, y: e.clientY });
      }}
      onMouseUp={() => setDragStart(null)}
      onMouseLeave={() => setDragStart(null)}
      style={{
        position: 'absolute',
        left: node.position.x,
        top: node.position.y,
        width: 200,
        padding: 12,
        background: 'var(--paper)',
        border: `1px solid ${selected ? 'var(--flame)' : 'var(--line)'}`,
        borderRadius: 'var(--r-3)',
        cursor: dragStart ? 'grabbing' : 'grab',
        boxShadow: selected ? 'var(--sh-focus)' : 'var(--sh-sm)',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ color: 'var(--flame)', display: 'flex' }}>
          <IconCmp size={13} />
        </span>
        <span
          className="mono"
          style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
        >
          {node.kind}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 550, marginBottom: 2 }}>{label}</div>
      {subtitle && (
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function Inspector({
  node,
  onConfigChange,
  onDelete,
}: {
  node: FlowNode;
  onChange: (patch: Partial<FlowNode>) => void;
  onConfigChange: (patch: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0 12px' }}>
        <div style={{ fontSize: 15, fontWeight: 600, textTransform: 'capitalize' }}>{node.kind}</div>
        <Button variant="ghost" size="sm" icon aria-label="Delete node"
          onClick={onDelete}
          title="Delete node">
          <Icon.trash size={12} />
        </Button>
      </div>

      {node.kind === 'trigger' && (
        <>
          <Field label="Source">
            <Select
              
              value={node.config.source}
              onChange={(e) => onConfigChange({ source: e.target.value })}>
              <option value="webhook">webhook</option>
              <option value="shopify">shopify</option>
              <option value="stripe">stripe</option>
              <option value="sheets">sheets</option>
              <option value="schedule">schedule</option>
            </Select>
          </Field>
          <Field label="Event">
            <Input
              
              value={node.config.event}
              onChange={(e) => onConfigChange({ event: e.target.value })}
              placeholder="order.paid" />
          </Field>
        </>
      )}

      {node.kind === 'send' && (
        <>
          <Field label="Template ID">
            <Input mono
              
              value={node.config.templateId}
              onChange={(e) => onConfigChange({ templateId: e.target.value })}
              placeholder="UUID of template" />
          </Field>
          <Field label="Provider connection (optional)">
            <Input mono
              
              value={node.config.providerConnectionId ?? ''}
              onChange={(e) =>
                onConfigChange({
                  providerConnectionId: e.target.value ? e.target.value : undefined,
                })
              }
              placeholder="UUID of provider connection (default if blank)" />
          </Field>
        </>
      )}

      {node.kind === 'delay' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
          <Field label="Amount">
            <Input
              
              type="number"
              value={node.config.amount}
              onChange={(e) => onConfigChange({ amount: Math.max(1, Number(e.target.value) || 1) })} />
          </Field>
          <Field label="Unit">
            <Select
              
              value={node.config.unit}
              onChange={(e) => onConfigChange({ unit: e.target.value })}>
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </Select>
          </Field>
        </div>
      )}

      {node.kind === 'split' && (
        <Field label="Branches">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {node.config.branches.map((b, i) => (
              <div
                key={b.id}
                className="mf-card"
                style={{
                  padding: 10,
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Input
                  
                  value={b.label}
                  onChange={(e) => {
                    const branches = node.config.branches.map((x, j) =>
                      j === i ? { ...x, label: e.target.value } : x,
                    );
                    onConfigChange({ branches });
                  }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Input
                    
                    type="number"
                    value={b.weight}
                    onChange={(e) => {
                      const weight = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      const branches = node.config.branches.map((x, j) =>
                        j === i ? { ...x, weight } : x,
                      );
                      onConfigChange({ branches });
                    }} />
                  <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>%</span>
                </div>
              </div>
            ))}
          </div>
        </Field>
      )}

      {node.kind === 'condition' && (
        <>
          <Field label="Path">
            <Input mono
              
              value={node.config.path}
              onChange={(e) => onConfigChange({ path: e.target.value })}
              placeholder="payload.order.total" />
          </Field>
          <Field label="Operator">
            <Select
              
              value={node.config.operator}
              onChange={(e) => onConfigChange({ operator: e.target.value })}>
              <option value="equals">equals</option>
              <option value="not_equals">not_equals</option>
              <option value="contains">contains</option>
              <option value="gt">gt</option>
              <option value="lt">lt</option>
              <option value="exists">exists</option>
            </Select>
          </Field>
          {node.config.operator !== 'exists' && (
            <Field label="Value">
              <Input
                
                value={node.config.value ?? ''}
                onChange={(e) => onConfigChange({ value: e.target.value })} />
            </Field>
          )}
        </>
      )}

      {node.kind === 'end' && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '6px 0' }}>
          Terminal node — no configuration.
        </div>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function describeNode(n: FlowNode): { label: string; subtitle?: string } {
  switch (n.kind) {
    case 'trigger':
      return { label: `${n.config.source} · ${n.config.event}`, subtitle: 'Entry point' };
    case 'send':
      return { label: 'Send template', subtitle: shortId(n.config.templateId) };
    case 'delay':
      return { label: `Wait ${n.config.amount}${unitSuffix(n.config.unit)}`, subtitle: 'Delay' };
    case 'split': {
      const total = n.config.branches.reduce((acc, b) => acc + b.weight, 0);
      return {
        label: `${n.config.branches.length}-way split`,
        subtitle: `${n.config.branches.map((b) => b.weight).join(' / ')} (sum ${total})`,
      };
    }
    case 'condition':
      return { label: `${n.config.path} ${n.config.operator} ${n.config.value ?? ''}`.trim(), subtitle: 'Condition' };
    case 'end':
      return { label: 'End', subtitle: 'Terminal' };
  }
}

function unitSuffix(unit: 'minutes' | 'hours' | 'days'): string {
  return unit === 'minutes' ? 'm' : unit === 'hours' ? 'h' : 'd';
}

function shortId(id: string): string {
  return id.length >= 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function nextPosition(nodes: FlowNode[]): { x: number; y: number } {
  if (nodes.length === 0) return { x: 80, y: 120 };
  const rightmost = Math.max(...nodes.map((n) => n.position.x));
  return { x: rightmost + 240, y: 120 };
}

function defaultNode(id: string, kind: FlowNodeKind, position: { x: number; y: number }): FlowNode {
  switch (kind) {
    case 'trigger':
      return { id, kind, position, config: { source: 'webhook', event: 'event.received' } };
    case 'send':
      return {
        id,
        kind,
        position,
        config: { templateId: '00000000-0000-0000-0000-000000000000' },
      };
    case 'delay':
      return { id, kind, position, config: { amount: 1, unit: 'hours' } };
    case 'split':
      return {
        id,
        kind,
        position,
        config: {
          branches: [
            { id: `${id}-a`, label: 'Variant A', weight: 50 },
            { id: `${id}-b`, label: 'Variant B', weight: 50 },
          ],
        },
      };
    case 'condition':
      return { id, kind, position, config: { path: 'payload.field', operator: 'equals', value: '' } };
    case 'end':
      return { id, kind, position, config: {} };
  }
}

function relativeTime(d: Date): string {
  const diff = Date.now() - d.getTime();
  const s = Math.round(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}

/**
 * Sticky footer strip showing the most recent runs of this automation.
 * Polls /automations/:id/runs every 5s. Pulse-animates rows whose status is
 * non-terminal so the user knows the system is still waiting on n8n.
 */
function RunsStrip({ runs }: { runs: AutomationRun[] }) {
  if (runs.length === 0) {
    return (
      <div style={runsStripStyle}>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: 0.06 }}>
          NO RUNS YET — TRIGGER THE AUTOMATION TO SEE EXECUTION HISTORY
        </span>
      </div>
    );
  }
  const view = runs.slice(0, 8);
  return (
    <div style={runsStripStyle}>
      <span
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, marginRight: 8 }}
      >
        RECENT RUNS
      </span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
        {view.map((r) => {
          const live = r.status === 'queued' || r.status === 'running';
          return (
            <Chip
              key={r.id}
              
              title={`${r.status} · started ${new Date(r.startedAt).toLocaleString()}${
                r.n8nExecutionId ? ` · n8n:${r.n8nExecutionId}` : ''
              }`}
              style={{
                background: chipBg(r.status),
                color: chipFg(r.status),
                fontSize: 11,
                padding: '3px 8px',
                animation: live ? 'mfPulse 1.4s ease-in-out infinite' : undefined,
                border: `1px solid ${chipBorder(r.status)}`,
              }}>
              <span className="dot" style={{ background: chipFg(r.status) }} />
              {r.status} · {timeOnly(r.startedAt)}
            </Chip>
          );
        })}
      </div>
    </div>
  );
}

const runsStripStyle: CSSProperties = {
  position: 'sticky',
  bottom: 0,
  background: 'var(--paper)',
  borderTop: '1px solid var(--line)',
  padding: '8px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  zIndex: 5,
};

function chipBg(status: AutomationRun['status']) {
  switch (status) {
    case 'success':
      return 'var(--spark-soft, rgba(0, 160, 100, 0.12))';
    case 'failed':
      return 'var(--flame-soft)';
    case 'running':
      return 'rgba(60, 130, 255, 0.12)';
    default:
      return 'var(--paper-2)';
  }
}
function chipFg(status: AutomationRun['status']) {
  switch (status) {
    case 'success':
      return 'var(--spark, #2c8a55)';
    case 'failed':
      return 'var(--flame-2, #c2410c)';
    case 'running':
      return 'rgb(40, 95, 200)';
    default:
      return 'var(--ink-2)';
  }
}
function chipBorder(status: AutomationRun['status']) {
  switch (status) {
    case 'success':
      return 'rgba(0, 160, 100, 0.35)';
    case 'failed':
      return 'var(--flame-line)';
    case 'running':
      return 'rgba(60, 130, 255, 0.35)';
    default:
      return 'var(--line)';
  }
}

function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
